/* "Quick $15" pill in the Vaults header: $15 from a Job Loss fund → Spending, $15 fee → Restock pot. Confetti on success. */
import { useCallback, useEffect, useState } from "react";
import confetti from "canvas-confetti";
import { api, fmtMoney, formatErr } from "@/lib/api";
import { Zap, Loader2, X } from "lucide-react";

export function fireSparkle() {
  const base = { particleCount: 70, spread: 75, startVelocity: 38, gravity: 0.9, ticks: 160, scalar: 0.9,
    colors: ["#2f4f3e", "#c9a25c", "#b5694f", "#f4efe6", "#7a8f7a"], disableForReducedMotion: true };
  confetti({ ...base, origin: { x: 0.5, y: 0.35 } });
  setTimeout(() => confetti({ ...base, particleCount: 40, angle: 60, origin: { x: 0.2, y: 0.5 } }), 120);
  setTimeout(() => confetti({ ...base, particleCount: 40, angle: 120, origin: { x: 0.8, y: 0.5 } }), 120);
}

export default function Quick15Pill({ onDone }) {
  const [st, setSt] = useState(null);
  const [open, setOpen] = useState(false);
  const [vaultId, setVaultId] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = useCallback(() => api.get("/quick15/status").then((r) => { setSt(r.data); setVaultId((id) => id || r.data.vaults[0]?.id || ""); }).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  if (!st?.enabled) return null;
  const left = st.remaining_today;

  const go = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/quick15/withdraw", { vault_id: vaultId });
      fireSparkle();
      setOpen(false);
      onDone?.(`${fmtMoney(data.withdrawn)} moved to Spending · ${fmtMoney(data.fee)} fee tucked into your Restock pot (${fmtMoney(data.pot.balance)}). ${data.remaining_today} quick pull${data.remaining_today === 1 ? "" : "s"} left today.`);
      load();
    } catch (e) { setErr(formatErr(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="relative" data-testid="quick15">
      <button onClick={() => { setOpen((o) => !o); setErr(""); }} disabled={left === 0}
        title={left === 0 ? "All 3 quick pulls used today" : "$15 out · $15 fee to Restock pot"}
        className="inline-flex items-center gap-1.5 rounded-full border border-gold text-gold-deep px-3 py-1.5 text-xs font-medium hover:bg-gold hover:text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        data-testid="quick15-btn">
        <Zap className="w-3.5 h-3.5" strokeWidth={2} /> Quick $15
        <span className="font-mono text-[10px] opacity-80" data-testid="quick15-remaining">{left}/{st.daily_limit} left</span>
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 z-30 w-80 bg-cream border border-sand rounded-xl shadow-lg p-4 text-left" data-testid="quick15-panel">
          <div className="flex items-start justify-between gap-2">
            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-gold-deep">Quick $15 · $15 fee</div>
            <button onClick={() => setOpen(false)} className="text-sage hover:text-ink" data-testid="quick15-close"><X className="w-3.5 h-3.5" /></button>
          </div>
          <p className="text-xs text-moss mt-1.5">$15 leaves your rainy-day fund for Spending, and a $15 fee moves from Spending into the <strong>Restock pot</strong> on your Inventory page. Up to 3 a day.</p>
          {st.vaults.length === 0 ? (
            <div className="mt-3 text-xs text-clay" data-testid="quick15-no-vault">No rainy-day fund with at least $15.</div>
          ) : (
            <>
              <select value={vaultId} onChange={(e) => setVaultId(e.target.value)} className="mt-3 w-full bg-white border border-sand rounded-md px-2 py-1.5 text-sm" data-testid="quick15-vault">
                {st.vaults.map((v) => <option key={v.id} value={v.id}>{v.name} · {fmtMoney(v.balance)}</option>)}
              </select>
              <button onClick={go} disabled={busy || !vaultId} className="mt-3 w-full bg-gold text-white rounded-md px-3 py-2 text-xs font-medium hover:bg-gold-deep disabled:opacity-50 inline-flex items-center justify-center gap-1.5" data-testid="quick15-confirm">
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5" />} Pull $15 · pay $15 fee
              </button>
            </>
          )}
          {err && <div className="mt-2 text-xs text-clay" data-testid="quick15-error">{err}</div>}
          <div className="mt-2 text-[10px] text-sage font-mono">Restock pot: {fmtMoney(st.pot.balance)}</div>
        </div>
      )}
    </div>
  );
}
